/**
 * <PdfDisplay> — la visionneuse PDF du web (molecule, L0.1).
 *
 * Le PDF est dessiné par pdf.js (`<PdfPages>`), sans le visionneur du navigateur : sans lui, l'iframe
 * d'avant laissait l'élève devant « Il n'y a pas d'aperçu disponible » (`DECISIONS.md`, 22/10/2026).
 * Toutes ses pages défilent, « − » / « + » zooment ; « Ouvrir » (nouvel onglet, où le navigateur
 * imprime) et « Télécharger » restent dans la barre, comme dans le visionneur qu'elle remplace. Le
 * chargement et l'échec sont ceux de `usePdfDocument` ; l'échec propose les deux mêmes gestes
 * (`<PdfFallback>`). Close chrome comes from `<Overlay withCloseButton>`.
 */
import React, { useState } from "react"
import { View } from "react-native"
import { StyleSheet, useUnistyles } from "react-native-unistyles"

import { IconButton } from "../../atoms/Buttons/IconButton"
import { Row } from "../../atoms/containers/Row"
import { Overlay } from "../../atoms/Overlay"
import { DS_SCROLL_ATTRIBUTE } from "../../utils/webScrollbars"
import { PdfFallback } from "./PdfFallback.web"
import { PdfPageSkeleton } from "./PdfPageSkeleton"
import { PdfPages } from "./PdfPages.web"
import { downloadPdf, openPdfInNewTab } from "./pdfjs"
import { usePdfDocument } from "./usePdfDocument.web"

export type PdfDisplayProps = {
    visible: boolean
    uri: string
    onClose: () => void
    /** Le PDF ne s'est pas chargé : la cause que rend pdf.js. Absent : rien. */
    onError?: (cause: unknown) => void
    testID?: string
}

/** Les crans du zoom ; `1` : la page à la largeur de la visionneuse. */
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3] as const
const FIT_STEP = ZOOM_STEPS.indexOf(1)

/** Les boutons de la barre, posée sur le bas : la dernière page s'arrête au-dessus d'elle. */
const BUTTON_SIZE = 44

const TEXTS = {
    zoomIn: "Zoomer",
    zoomOut: "Dézoomer",
    open: "Ouvrir le PDF dans un nouvel onglet",
    download: "Télécharger le PDF",
}

/** La barrette d'un défilement du design system (`webScrollbars`), sur un `div` brut. */
const DS_SCROLL = { [DS_SCROLL_ATTRIBUTE]: "true" }

export function PdfDisplay(props: PdfDisplayProps) {
    const { visible, uri, onClose, onError, testID } = props
    const [boxWidth, setBoxWidth] = useState(0)
    // L'overlay ne monte son contenu qu'ouvert : le document se charge à l'ouverture, se libère à la
    // fermeture, et le zoom repart de la largeur à chaque PDF.
    return (
        <Overlay visible={visible} onRequestClose={onClose} withCloseButton placement="center" testID={testID}>
            <View style={styles.container} onLayout={(event) => setBoxWidth(event.nativeEvent.layout.width)}>
                {uri !== "" && <PdfViewer key={uri} uri={uri} boxWidth={boxWidth} onError={onError} />}
            </View>
        </Overlay>
    )
}

type PdfViewerProps = { uri: string; boxWidth: number; onError?: (cause: unknown) => void }

function PdfViewer({ uri, boxWidth, onError }: PdfViewerProps) {
    const load = usePdfDocument(uri, onError)
    const { theme } = useUnistyles()
    const [zoomStep, setZoomStep] = useState(FIT_STEP)
    const padding = theme.spacing["3"]

    if (load.status === "failed") {
        return (
            <View style={styles.centered}>
                <PdfFallback uri={uri} />
            </View>
        )
    }
    if (load.status === "loading") {
        return (
            <View style={styles.centered}>
                <PdfPageSkeleton />
            </View>
        )
    }
    return (
        <View style={styles.fill}>
            <div
                {...DS_SCROLL}
                style={{
                    position: "absolute",
                    inset: 0,
                    overflow: "auto",
                    padding,
                    paddingBottom: BUTTON_SIZE + 2 * padding,
                }}
            >
                <PdfPages
                    doc={load.doc}
                    pages={load.pages}
                    boxWidth={boxWidth - 2 * padding}
                    zoom={ZOOM_STEPS[zoomStep] ?? 1}
                />
            </div>
            {/* La bande prend toute la largeur : seuls ses boutons prennent le toucher, le reste défile.
                En prop, que react-native-web traduit : `box-none` n'est pas une valeur CSS. */}
            <View style={styles.nav} pointerEvents="box-none">
                <Row justify="center" gap="2">
                    <IconButton
                        iconName="search-minus"
                        color="text-muted"
                        size={BUTTON_SIZE}
                        disabled={zoomStep === 0}
                        onPress={() => setZoomStep((step) => Math.max(0, step - 1))}
                        accessibilityLabel={TEXTS.zoomOut}
                    />
                    <IconButton
                        iconName="search-plus"
                        color="text-muted"
                        size={BUTTON_SIZE}
                        disabled={zoomStep === ZOOM_STEPS.length - 1}
                        onPress={() => setZoomStep((step) => Math.min(ZOOM_STEPS.length - 1, step + 1))}
                        accessibilityLabel={TEXTS.zoomIn}
                    />
                    <IconButton
                        iconName="external-link-alt"
                        color="text-muted"
                        size={BUTTON_SIZE}
                        onPress={() => openPdfInNewTab(uri)}
                        accessibilityLabel={TEXTS.open}
                    />
                    <IconButton
                        iconName="download"
                        color="text-muted"
                        size={BUTTON_SIZE}
                        onPress={() => void downloadPdf(uri)}
                        accessibilityLabel={TEXTS.download}
                    />
                </Row>
            </View>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    container: {
        width: "95%" as const,
        height: "95%" as const,
        backgroundColor: theme.colors["surface-subtle"],
        overflow: "hidden" as const,
    },
    fill: {
        flex: 1,
    },
    centered: {
        flex: 1,
        justifyContent: "center" as const,
        alignItems: "center" as const,
        padding: theme.spacing["5"],
    },
    nav: {
        position: "absolute" as const,
        left: 0,
        right: 0,
        bottom: theme.spacing["3"],
        alignItems: "center" as const,
    },
}))
